/*
 * Top level module for the special reviewer module, which allows users
 * to work on tasks when they are forbidden from accessing the rest of the
 * site.
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "moment",
  "./reviewerView",
  "./reviewerList/reviewerListModule",
  "./reviewerArea/reviewerAreaModule",
  "./reviewerListToggle/reviewerListToggleModule",
  "modules/tasks/tasksModel",
  "modules/tasks/taskWizardModal/taskWizardModalModule",
  "./reviewTypeToggle/reviewTypeToggleModule",
  "modules/tasks/tasksArea/tasksAreaModule",
  "modules/tasks/tasksList/tasksListModule",
  "./reviewerTasksListToggle/reviewerTasksListToggleModule",
  "./reviewerOtherTasksListToggle/reviewerOtherTasksListToggleModule",
], function (
  Marionette,
  Backbone,
  $,
  Properties,
  Messenger,
  moment,
  ReviewerView,
  ReviewListModule,
  ReviewAreaModule,
  ReviewListToggleModule,
  TaskModel,
  TaskWizardModalModule,
  ReviewTypeToggleModule,
  TasksAreaModule,
  TasksListModule,
  TasksListToggleModule,
  OtherTasksListToggleModule,
) {
  "use strict";

  const ReviewerController = Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "reviewer";
      this.region = options.region;
      this._setUpVents();
      //set up a router to test this area
      this.router = new Router({
        controller: this,
      });
    },
    show: function () {
      const kbId = Messenger.reqres.request("active:kb");
      let canCreateTask = Messenger.reqres.request("can:create:task");
      let model = new Backbone.Model({
        canCreateTask: canCreateTask,
      });
      Backbone.history.navigate(kbId + "/reviewer");
      document.title = $.t("menuBar.tasks") + " - " + Properties.applicationName;
      //nothing to save...yet
      Messenger.onSave(function () {
        //let the list handle the save
        let activeView = this.reviewTypeToggle.getActiveView();
        if (activeView == ReviewTypeToggleModule.MY_TASKS) {
          console.log("saving reviewer");
          this.reviewerList.save();
        } else {
          console.log("saving tasks");
          this.tasksList.save();
        }
      }, this);

      //should probably check if tasks are enabled for the active KB

      this.layout = new ReviewerView.reviewerLayout({ model: model });
      this.reviewerList = this.addSubModule(new ReviewListModule({ region: this.layout.list }));
      this.tasksList = this.addSubModule(new TasksListModule({ region: this.layout.list }));
      this.addSubModule(new ReviewAreaModule({ region: this.layout.area }));
      this.addSubModule(new TasksAreaModule({ region: this.layout.area, allowDelete: true }));
      this.reviewerListToggle = this.addSubModule(
        new ReviewListToggleModule({ region: this.layout.listToggle }),
      );
      this.tasksListToggle = this.addSubModule(
        new TasksListToggleModule({ region: this.layout.listToggle }),
      );
      this.reviewTypeToggle = this.addSubModule(
        new ReviewTypeToggleModule({ region: this.layout.reviewTypeToggle, model: model }),
      );
      this.otherTasksListToggle = this.addSubModule(
        new OtherTasksListToggleModule({ region: this.layout.listToggle }),
      );
      this.layout.on(
        "show",
        function () {
          this.reviewTypeToggle.show();
          let activeView = this.reviewTypeToggle.getActiveView();
          if (activeView == ReviewTypeToggleModule.MY_TASKS) {
            this.reviewerListToggle.show();
            this.reviewerList.show();
          } else if (activeView == ReviewTypeToggleModule.ASSIGNED_TASKS) {
            this.tasksListToggle.show();
            this._showAssignedTasksList(TaskModel.STATUS.NOT_STARTED);
          } else {
            this.otherTasksListToggle.show();
            this._showOtherTasksList(TaskModel.STATUS.NOT_STARTED);
          }
        },
        this,
      );
      this.layout.on("add:task", this._onAddTask, this);
      this.showRegion(this.layout);
    },
    _onAddTask: function () {
      let task = Messenger.reqres.request("task:createAdvanced");
      Messenger.vent.trigger("show:modal", TaskWizardModalModule, {}, task, {
        closeButton: false,
        closeOnBackgroundClick: false,
        title: "task.wizard.title",
        trackInteraction: {
          action: "Add Advanced Task",
          object: "Task",
          source: "Task",
        },
      });
    },
    _route: function (kbId) {
      Messenger.command.execute("set:active:kb", kbId);
      Messenger.command.execute("change:menu", "tasks");
      this.show();
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler(
        "get:selected:task",
        function () {
          return this.task;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "get:selected:topic",
        function () {
          return this.topic;
        },
        this,
      );
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "show:reviewer",
        function () {
          this.show();
        },
        this,
      );
      Messenger.vent.on("show:tasks:list", this._showAssignedTasksList, this);
      Messenger.vent.on("show:other:tasks:list", this._showOtherTasksList, this);
      Messenger.vent.on(
        "show:reviewType:assignedTasks",
        function () {
          // This can be called in a different module, so only do something if this module has been shown at least once
          if (this.tasksListToggle) {
            Messenger.vent.trigger("deselect:review");
            Messenger.vent.trigger("deselect:task");
            this.tasksListToggle.show();
            Messenger.vent.trigger("show:tasks:list", TaskModel.STATUS.NOT_STARTED);
          }
        },
        this,
      );
      Messenger.vent.on(
        "show:reviewType:myTasks",
        function () {
          // This can be called in a different module, so only do something if this module has been shown at least once
          if (this.reviewerListToggle) {
            Messenger.vent.trigger("deselect:review");
            Messenger.vent.trigger("deselect:task");
            this.reviewerListToggle.show();
            Messenger.vent.trigger("show:reviewer:list", TaskModel.STATUS.NOT_STARTED);
          }
        },
        this,
      );
      Messenger.vent.on(
        "show:reviewType:otherTasks",
        function () {
          // This can be called in a different module, so only do something if this module has been shown at least once
          if (this.otherTasksListToggle) {
            Messenger.vent.trigger("deselect:review");
            Messenger.vent.trigger("deselect:task");
            this.otherTasksListToggle.show();
            Messenger.vent.trigger("show:other:tasks:list", TaskModel.STATUS.NOT_STARTED);
          }
        },
        this,
      );
    },
    _showAssignedTasksList: function (statusId) {
      if (this.tasksList) {
        const login = Messenger.reqres.request("user").get("loginName");
        let tasks = Messenger.reqres.request("tasks:by:creator:and:status", login, statusId);
        this.tasksList.show(tasks, statusId, false);
      }
    },
    _showOtherTasksList: function (statusId) {
      if (this.tasksList) {
        const login = Messenger.reqres.request("user").get("loginName");
        let tasks = Messenger.reqres.request("tasks:by:other:creator:and:status", login, statusId);
        this.tasksList.show(tasks, statusId, false);
      }
    },
  });

  let Router = Marionette.AppRouter.extend({
    appRoutes: {
      ":kbId/reviewer": "_route",
    },
  });

  return ReviewerController;
});
